import BackButton from "components/back-button/back-button.component";
import { selectAppealCode, selectSource } from "redux/form/form.selector";
import {
  BottomButtonsContainer,
  FormPartContainer,
  NextStepButton,
  SectionHeading,
} from "global";
import { FC, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { selectPresId } from "redux/presValue/presValue.selector";
import {
  resetFormReducer,
  updateFormLanguage,
  updateFormErrors,
  updateSecuritiesItems,
} from "redux/form/form.reducer";
import {
  selectFormErrors,
  selectFormLanguage,
  selectSecuritiesInfo,
  selectSecuritiesItems,
} from "redux/form/form.selector";
import { selectCurrentForm } from "redux/helpers/helpers.selector";
import {
  updateCurrentForm,
  updateIsFormDisabled,
  updatePopup,
} from "redux/helpers/helpers.reducer";
import { selectPopup } from "redux/helpers/helpers.selector";
import { selectSession } from "redux/helpers/helpers.selector";
import { selectDonorInfo } from "redux/form/form.selector";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { emptySecurityItem } from "utils/form-constants";
import { validate, scrollToTheError } from "utils/helper-functions";
import { gosSecurityItemSchema } from "utils/validation-schemas";
import { popupMessages } from "utils/form-constants";
import { ValidationError } from "yup";
import {
  AddSecurityButton,
  SecuritiesContainer,
} from "./gos-securities-step.styles";
import SecurityItemForm from "./security-item-form.component";
import serviceCallsAPI from "utils/serviceCallsAPI";
import datalayerAPI from "utils/datalayerAPI";

const GosSecuritiesStep: FC = () => {
  // LOCAL STATE
  const [isLoading, setIsLoading] = useState(false);

  // GLOBAL STATE
  const dispatch = useAppDispatch();
  const currentForm = useAppSelector(selectCurrentForm);
  const formErrors = useAppSelector(selectFormErrors);
  const securitiesItems = useAppSelector(selectSecuritiesItems);
  const securitiesInfo = useAppSelector(selectSecuritiesInfo);
  const donorInfo = useAppSelector(selectDonorInfo);
  const formLanguage = useAppSelector(selectFormLanguage);
  const presid = useAppSelector(selectPresId);
  const appealCode = useAppSelector(selectAppealCode);
  const session = useAppSelector(selectSession);
  const source = useAppSelector(selectSource);
  const popup = useAppSelector(selectPopup);
  const { t } = useTranslation();

  const handleAddSecurity = () => {
    dispatch(
      updateSecuritiesItems([...securitiesItems, { ...emptySecurityItem }]),
    );
  };

  const handleNextStep = async () => {
    const itemErrors: { [key: string]: string }[] = [];
    let hasErrors = false;

    for (const item of securitiesItems) {
      try {
        await validate(item, gosSecurityItemSchema, [
          ["numberOfUnits", "approximateDollarValue"],
        ]);
        itemErrors.push({});
      } catch (error: unknown) {
        hasErrors = true;
        const errors: { [key: string]: string } = {};
        (error as ValidationError).inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });
        itemErrors.push(errors);
      }
    }

    if (hasErrors) {
      // Find first error to scroll to
      const firstErrorIndex = itemErrors.findIndex(
        (errors) => Object.keys(errors).length > 0,
      );
      if (firstErrorIndex >= 0) {
        const firstErrorField = Object.keys(itemErrors[firstErrorIndex])[0];
        scrollToTheError(`${firstErrorField}-${firstErrorIndex}`);
      }

      dispatch(
        updateFormErrors({ ...formErrors, securitiesItems: itemErrors }),
      );
      dispatch(
        updatePopup({
          ...popup,
          message: popupMessages.errorsFixed,
          isError: true,
          isActive: true,
          isLoading: false,
        }),
      );
      return;
    }

    setIsLoading(true);
    // Process donation
    const securities = securitiesItems.map((item) => ({
      financialInstitution:
        securitiesInfo.selfDirectedPlatform || securitiesInfo.brokerageName,
      securitiesAccountNumber: securitiesInfo.securitiesAccountNumber,
      advisorFirstName: securitiesInfo.advisorFirstName || undefined,
      advisorLastName: securitiesInfo.advisorLastName || undefined,
      advisorPhoneNumber: securitiesInfo.advisorPhoneNumber || undefined,
      advisorEmail: securitiesInfo.advisorEmail || undefined,
      securitiesName: item.securityName,
      securitiesType: item.securityType,
      cusipSymbol: item.cusipOrSymbol,
      numberOfUnits: +item.numberOfUnits || undefined,
      estimatedDollarValue: +item.approximateDollarValue || undefined,
    }));
    const response = await serviceCallsAPI.callGosSubmit({
      ...donorInfo,
      language: formLanguage,
      sessionid: session.sessionID,
      securities,
      donationType: "individual",
      giftType: "SEC",
      presid,
      appealCode,
      source,
    });

    if (response.status !== 200) {
      setIsLoading(false);
      dispatch(updateIsFormDisabled(false));

      dispatch(
        updatePopup({
          ...popup,
          isError: true,
          isActive: true,
          message: popupMessages.somethingWentWrong,
          isLoading: false,
        }),
      );

      return;
    }

    setIsLoading(false);
    dispatch(updateIsFormDisabled(false));

    datalayerAPI.handleGosFlowComplete();
    dispatch(
      updatePopup({
        ...popup,
        isError: false,
        isActive: true,
        message: popupMessages.securitiesSuccess,
        isLoading: false,
        suppressDataLayerEvent: true,
      }),
    );
    dispatch(resetFormReducer());
    dispatch(updateFormLanguage(formLanguage));
    dispatch(updateCurrentForm(0));
  };

  return (
    <FormPartContainer currentForm={currentForm} formID={2}>
      <SectionHeading>{t("Securities to donate")}</SectionHeading>

      <SecuritiesContainer>
        {securitiesItems.map((_, index) => (
          <Fragment key={index}>
            <SecurityItemForm index={index} />
            {index < securitiesItems.length - 1 && <hr color="#D9D9D9" />}
          </Fragment>
        ))}

        {securitiesItems.length < 5 && (
          <AddSecurityButton type="button" onClick={handleAddSecurity}>
            + {t("Add another security")}
          </AddSecurityButton>
        )}

        <hr color="#D9D9D9" />
      </SecuritiesContainer>

      <BottomButtonsContainer>
        <BackButton />

        <NextStepButton disabled={isLoading} onClick={handleNextStep}>
          {isLoading ? t("Loading") : t("Complete Donation")}
        </NextStepButton>
      </BottomButtonsContainer>
    </FormPartContainer>
  );
};

export default GosSecuritiesStep;
